import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, PenTool, CheckCircle2, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/util/Reveal";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/lib/usePageSeo";
import { ServiceHelpLinks } from "@/components/util/ServiceHelpLinks";

/**
 * Branding Situation-First Entry Gate.
 *
 * Shown at /services/logo-brand-identity when no ?path= query param is
 * set. Presents the customer with a single, clear "what do you need
 * help with?" choice before any commercial ladder or configurator is
 * exposed. The chosen path becomes a URL param so React Router history
 * / back-forward navigation restores it naturally.
 *
 * Three paths:
 *   • path=new           → normal PackageBuilder (tier ladder only —
 *                          existing-logo standalone items are hidden)
 *   • path=existing      → ExistingLogoConfigurator (Recreate / Refresh /
 *                          BI-Existing-Logo → mandatory upload → review)
 *   • (complete brand)   → routes to /services?for=existing-brand
 *                          (contextual Services mode: compact intro,
 *                          Logo & Brand Identity hidden, downstream
 *                          creative services only, "View All Services"
 *                          escape at bottom)
 *
 * All copy + visuals reuse the existing Ur Designs card + button + type
 * system. This is a routing / decision screen, not a redesign.
 */
export const BrandingSituationGate = () => {
  const navigate = useNavigate();
  usePageSeo({
    title: "Logo & Brand Identity — Ur Designs",
    description: "Logo and brand identity design for Aruba businesses — pick the path that fits your project: new brand, existing logo refresh, or full downstream creative services.",
    path: "/services/logo-brand-identity",
  });

  const paths = [
    {
      id: "new",
      icon: Sparkles,
      title: "I need a new brand or logo",
      subtitle: "Starting fresh or looking for a completely new creative direction.",
      hint: "Logo Design · Brand Identity · Complete Brand System",
      cta: "Choose Your Package",
      testId: "branding-path-new",
      onClick: () => navigate("/services/logo-brand-identity?path=new"),
    },
    {
      id: "existing_logo",
      icon: PenTool,
      title: "I already have a logo",
      subtitle: "I want to improve it, recreate it or build my brand around it.",
      hint: "Vectorization · Refresh · Brand Identity — Existing Logo",
      cta: "See Your Options",
      testId: "branding-path-existing-logo",
      onClick: () => navigate("/services/logo-brand-identity?path=existing"),
    },
    {
      id: "complete_brand",
      icon: CheckCircle2,
      title: "I already have a complete brand",
      subtitle: "I need design, advertising or digital work using my existing branding.",
      hint: "Design · Campaigns · Packaging · 3D & More",
      cta: "Browse Creative Services",
      testId: "branding-path-complete",
      onClick: () => navigate("/services?for=existing-brand"),
    },
  ];

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <Reveal>
          <div className="mb-10 sm:mb-14 text-center">
            <div className="ur-eyebrow mb-4" data-testid="branding-gate-eyebrow">
              Logo &amp; Brand Identity
            </div>
            <h1
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4"
              data-testid="branding-gate-heading"
            >
              What do you need <span className="text-[#FF6600]">help with?</span>
            </h1>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto text-sm sm:text-base">
              Pick the option that matches where you are today — we'll only show
              services that actually fit your situation.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 items-stretch">
          {paths.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.id} delay={i * 90} className="h-full">
                <motion.button
                  type="button"
                  data-testid={p.testId}
                  onClick={p.onClick}
                  whileHover={{ y: -2 }}
                  className="group h-full w-full text-left bg-[#151515] rounded-2xl border border-[#262626] p-7 flex flex-col hover:border-[#FF6600]/50 focus:outline-none focus-visible:border-[#FF6600] focus-visible:ring-2 focus-visible:ring-[#FF6600]/40 transition-colors"
                >
                  <div className="w-12 h-12 grid place-items-center rounded-xl bg-[#1F1F1F] border border-[#262626] text-[#FF6600] mb-5 shrink-0">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#D1D1D1] leading-relaxed mb-5">
                    {p.subtitle}
                  </p>
                  <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#A3A3A3] mb-5">
                    {p.hint}
                  </div>
                  <div className="mt-auto flex items-center gap-2 text-sm font-bold text-[#FF6600] group-hover:gap-3 transition-all">
                    {p.cta}
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </motion.button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={280}>
          <div className="mt-10 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/contact")}
              data-testid="branding-gate-help"
              className="text-[#A3A3A3] hover:text-white"
            >
              Not sure? Talk to us first →
            </Button>
          </div>
        </Reveal>
        <ServiceHelpLinks className="pt-6" />
      </section>
  );
};

export default BrandingSituationGate;
